/**
 * Quick Reorder Panel - Feature B
 * Shows customer's last order for instant reordering
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { RotateCcw, Phone, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function QuickReorderPanel({ onAddToCart }) {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lastOrder, setLastOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchLastOrder = async () => {
    if (!phoneNumber || phoneNumber.length < 6) {
      toast.error('Telefonnummer zu kurz');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${API_BASE}/api/quick-reorder/last-order?customer_phone=${encodeURIComponent(phoneNumber)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLastOrder(data.last_order);
        toast.success(`Letzte Bestellung von ${data.customer.name} gefunden!`);
      } else {
        setLastOrder(null);
        toast.info('Keine vorherige Bestellung gefunden');
      }
    } catch (error) {
      toast.error('Fehler bei der Suche');
      setLastOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const reorderLastOrder = () => {
    if (!lastOrder || !lastOrder.items) return;
    
    // Add all items from last order to cart
    lastOrder.items.forEach(item => {
      onAddToCart && onAddToCart({
        name: item.name,
        price: item.unit_price || item.subtotal,
        quantity: item.quantity,
        from_reorder: true
      });
    });
    
    toast.success('Letzte Bestellung zum Warenkorb hinzugefügt!', {
      description: `${lastOrder.items.length} Artikel, ${lastOrder.total_amount?.toFixed(2)}€`
    });
    
    setLastOrder(null);
    setPhoneNumber('');
  };

  return (
    <div className="mb-4 p-4 bg-[#111C32]/60 rounded-xl border border-emerald-500/30">
      <div className="flex items-center gap-2 mb-3">
        <RotateCcw className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-semibold text-emerald-400 uppercase tracking-wide">Letzte Bestellung</span>
      </div>
      
      <div className="flex gap-2 mb-3">
        <Input
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && searchLastOrder()}
          placeholder="Telefonnummer eingeben..."
          className="flex-1 bg-[#0E1628] border-slate-700 text-white h-10"
        />
        <Button
          onClick={searchLastOrder}
          disabled={loading}
          size="sm"
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
        >
          <Phone className="w-4 h-4" />
        </Button>
      </div>
      
      {lastOrder && (
        <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-sm font-semibold text-white">#{lastOrder.order_number}</div>
              <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3" />
                {new Date(lastOrder.created_at).toLocaleString('de-DE')}
              </div>
            </div>
            <div className="text-emerald-400 font-bold">
              {lastOrder.total_amount?.toFixed(2)}€
            </div>
          </div>
          
          <div className="text-xs text-slate-300 mb-2">
            {lastOrder.items?.length} Artikel
          </div>
          
          <Button
            onClick={reorderLastOrder}
            size="sm"
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white"
          >
            <RotateCcw className="w-3 h-3 mr-2" />
            Zum Warenkorb hinzufügen
          </Button>
        </div>
      )}
    </div>
  );
}
